import { outputAudioConfig } from "./config";

export class AudioManager {
  private audioContext: AudioContext;
  private micStream: MediaStream | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private processorNode: ScriptProcessorNode | null = null;
  private gainNode: GainNode | null = null; // 用于静音输出，防止回声
  private masterGainNode: GainNode; // Main volume control
  private isRecording: boolean = false;
  private audioQueue: Float32Array[] = [];
  public isPlaying = false;
  private sampleRate = outputAudioConfig.sampleRate;

  // 用于时间调度的播放
  private nextPlayTime = 0; // 下一个音频块应该开始的时间
  private scheduledSources: AudioBufferSourceNode[] = []; // 已调度的音频源
  private isPaused = false; // 是否暂停播放
  private stopRequested = false; // 是否中止整个播放
  private audioCallback: ((audioData: ArrayBuffer) => void) | null = null;
  private logCallback: ((message: string, level?: 'info' | 'debug' | 'warning' | 'error') => void) | null = null;
  private onPlaybackStateChangeCallback: ((isSpeaking: boolean) => void) | null = null;
  private isStartingMic = false; // Prevent race condition during async getUserMedia
  private totalScheduledDurationMs = 0; // 当前 Session 累计调度的音频总时长 (ms)

  constructor() {
    // Do NOT force sampleRate here
    this.audioContext = new AudioContext();

    // Initialize Master Gain Node
    this.masterGainNode = this.audioContext.createGain();
    this.masterGainNode.gain.value = 1.0;
    this.masterGainNode.connect(this.audioContext.destination);

    // 明确初始化所有状态
    this.isRecording = false;
    this.isPlaying = false;
    this.isPaused = false;
    this.stopRequested = false;  // ✅ 关键：初始为 false
    this.nextPlayTime = 0;
    this.audioQueue = [];
    this.scheduledSources = [];

    console.log(`[AudioManager] Created with native sampleRate: ${this.audioContext.sampleRate}Hz`);
    console.log(`[AudioManager] Initial state: stopRequested=${this.stopRequested}, isPlaying=${this.isPlaying}`);
  }

  setAudioCallback(callback: (audioData: ArrayBuffer) => void) {
    this.audioCallback = callback;
  }

  setLogger(callback: (message: string, level?: 'info' | 'debug' | 'warning' | 'error') => void) {
    this.logCallback = callback;
  }

  setOnPlaybackStateChange(callback: (isSpeaking: boolean) => void) {
    this.onPlaybackStateChangeCallback = callback;
  }

  /**
   * 重置播放状态（用于新会话开始时）
   */
  resetPlaybackState() {
    console.log(`[AudioManager] Resetting playback state`);
    this.stopRequested = false;
    this.isPaused = false;
    const wasPlaying = this.isPlaying;
    this.isPlaying = false;
    if (wasPlaying && this.onPlaybackStateChangeCallback) {
      this.onPlaybackStateChangeCallback(false);
    }
    this.nextPlayTime = 0;
    this.audioQueue = [];

    // 停止所有已调度的音频
    for (const source of this.scheduledSources) {
      try {
        source.stop();
        source.disconnect();
      } catch (e) {
        // 忽略错误
      }
    }
    this.scheduledSources = [];
    this.loggedFirstChunk = false;

    console.log(`[AudioManager] Playback state reset complete`);
  }

  async startMicCapture() {
    if (this.isRecording || this.isStartingMic) {
      console.warn('[AudioManager] startMicCapture called but already recording or initializing');
      return;
    }

    this.isStartingMic = true;
    try {
      // 1. Reset Master Gain
      if (this.masterGainNode) {
        // Reset to 1.0 (Normal volume). User reported 1.2 was too loud.
        this.masterGainNode.gain.value = 1.0;
        console.log(`[AudioManager] Master Gain set to ${this.masterGainNode.gain.value}`);
      }

      // 2. CHECK EXISTING STREAM (Reuse if available)
      if (this.micStream) {
        const tracks = this.micStream.getAudioTracks();
        const activeTracks = tracks.filter(t => t.readyState === 'live');

        if (activeTracks.length === 0) {
          console.warn('[AudioManager] Existing stream tracks are ended/dead. Re-acquiring...');
          this.micStream = null;
          // Fall through to Step 3
        } else {
          console.log('[AudioManager] Reusing existing microphone stream (Unmuting)');
          tracks.forEach(track => {
            if (track.readyState === 'live') {
              track.enabled = true; // Unmute only live tracks
            }
          });

          // Ensure AudioContext is running
          if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
          }

          this.isRecording = true;
          return;
        }
      }

      // 3. New Stream Setup (First time only)
      // Use native sample rate for getUserMedia as well
      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      this.sourceNode = this.audioContext.createMediaStreamSource(this.micStream);
      this.processorNode = this.audioContext.createScriptProcessor(4096, 1, 1);
      this.processorNode.onaudioprocess = (event) => this.handleMicAudio(event);

      // 创建一个增益为 0 的 GainNode，这样可以保持音频处理链完整（触发 onaudioprocess）
      // 但不会实际播放麦克风声音（避免回声循环）
      this.gainNode = this.audioContext.createGain();
      this.gainNode.gain.value = 0; // 静音

      // 连接音频节点链：麦克风 → 处理器 → 增益（静音） → 扬声器
      this.sourceNode.connect(this.processorNode);
      this.processorNode.connect(this.gainNode);
      this.gainNode.connect(this.audioContext.destination);

      await this.audioContext.resume();
      this.isRecording = true;
      const msg = `✅ Mic capture started (Native Rate: ${this.audioContext.sampleRate}Hz -> Target: ${this.sampleRate}Hz)`;
      console.log(msg);
      if (this.logCallback) this.logCallback(msg, 'info');
    } finally {
      this.isStartingMic = false;
    }
  }

  private handleMicAudio(event: AudioProcessingEvent) {
    if (!this.isRecording) {
      // console.log('[AudioManager] handleMicAudio called but isRecording is false');
      return;
    }
    if (!this.audioCallback) {
      console.warn('[AudioManager] handleMicAudio called but no audioCallback set');
      return;
    }
    const inputBuffer = event.inputBuffer.getChannelData(0);

    // 智能音频门控：如果 AI 正在播放，只发送音量较大的音频（用户打断）
    if (this.isPlaying) {
      // 计算 RMS (Root Mean Square) 音量
      const rms = Math.sqrt(
        inputBuffer.reduce((sum, val) => sum + val * val, 0) / inputBuffer.length
      );

      // 阈值：0.02 (降低阈值，让更多打断信号通过)
      // 用户打断的声音通常会比回声更大
      const VOLUME_THRESHOLD = 0.02;

      if (rms < VOLUME_THRESHOLD) {
        // 音量太小，可能是回声，不发送
        return;
      }

      // 如果音量足够大，记录一下（便于调试）
      if (Math.random() < 0.01) { // 1% 采样率，避免日志过多
        const msg = `[AudioManager] AI播放时检测到较大音量: ${rms.toFixed(4)} (阈值: ${VOLUME_THRESHOLD})`;
        console.log(msg);
        if (this.logCallback) this.logCallback(msg, 'debug');
      }
    } else {
      // 当 AI 不播放时，也记录一下音量（用于调试 VAD 灵敏度）
      // 采样率降低一点，避免刷屏
      if (Math.random() < 0.005) {
        const rms = Math.sqrt(
          inputBuffer.reduce((sum, val) => sum + val * val, 0) / inputBuffer.length
        );
        if (rms > 0.01) {
          const msg = `[AudioManager] Mic Input RMS: ${rms.toFixed(4)}`;
          // console.log(msg); // Local log
          if (this.logCallback) this.logCallback(msg, 'debug');
        }
      }
    }

    // Downsample to 24000Hz if needed
    let processedData: Float32Array = inputBuffer;
    if (this.audioContext.sampleRate !== this.sampleRate) {
      processedData = this.downsampleBuffer(inputBuffer, this.audioContext.sampleRate, this.sampleRate);
    }

    const audioData = this.float32To16BitPCM(processedData);
    this.audioCallback(audioData);
  }

  private downsampleBuffer(buffer: Float32Array, sampleRate: number, outSampleRate: number): Float32Array {
    if (outSampleRate === sampleRate) {
      return buffer;
    }
    if (outSampleRate > sampleRate) {
      // Upsampling not supported/needed for this use case
      return buffer;
    }
    const sampleRateRatio = sampleRate / outSampleRate;
    const newLength = Math.round(buffer.length / sampleRateRatio);
    const result = new Float32Array(newLength);
    let offsetResult = 0;
    let offsetBuffer = 0;
    while (offsetResult < result.length) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
      // Use average value of skipped samples
      let accum = 0, count = 0;
      for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
        accum += buffer[i];
        count++;
      }
      result[offsetResult] = count > 0 ? accum / count : 0;
      offsetResult++;
      offsetBuffer = nextOffsetBuffer;
    }
    return result;
  }

  private float32To16BitPCM(float32Array: Float32Array) {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    let offset = 0;
    for (let i = 0; i < float32Array.length; i++, offset += 2) {
      let s = Math.max(-1, Math.min(1, float32Array[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return buffer;
  }

  private loggedFirstChunk = false;

  async playAudio(arrayBuffer: ArrayBuffer) {
    let float32: Float32Array;

    // Azure and Huoshan both send PCM16 (16-bit signed integer, little-endian)
    // Always treat incoming audio as PCM16 and convert to Float32 for playback
    if (arrayBuffer.byteLength % 2 === 0) {
      // PCM16: 2-byte aligned
      const dataView = new DataView(arrayBuffer);
      const pcm16 = new Int16Array(arrayBuffer.byteLength / 2);
      for (let i = 0; i < pcm16.length; i++) {
        pcm16[i] = dataView.getInt16(i * 2, true); // little-endian
      }
      float32 = new Float32Array(pcm16.length);
      for (let i = 0; i < pcm16.length; i++) {
        float32[i] = pcm16[i] / 32768.0; // Convert to -1.0 to 1.0 range
      }

      // Log first chunk for debugging
      if (!this.loggedFirstChunk) {
        this.loggedFirstChunk = true;
        const samples = pcm16.length;
        const duration = samples / this.sampleRate;
        console.log(`[AudioManager] First audio chunk: ${arrayBuffer.byteLength} bytes, ${samples} samples, ${duration.toFixed(3)}s`);
        console.log(`[AudioManager] Sample rate: ${this.sampleRate}Hz, AudioContext: ${this.audioContext.sampleRate}Hz`);

        // Check for sample rate mismatch
        if (this.audioContext.sampleRate !== this.sampleRate) {
          console.log(`ℹ️ Playback resampling active: ${this.sampleRate}Hz content -> ${this.audioContext.sampleRate}Hz context`);
        }
      }
    } else {
      console.error("❌ 音频格式错误：不是2字节对齐的PCM16:", arrayBuffer.byteLength);
      return;
    }

    // 直接调度播放，不放入队列
    this.scheduleAudioChunk(float32);
  }

  private scheduleAudioChunk(float32: Float32Array) {
    if (this.stopRequested || this.isPaused) {
      // 如果已停止或暂停，先放入队列等待
      console.log(`⏸️ Audio chunk queued (stopped=${this.stopRequested}, paused=${this.isPaused})`);
      this.audioQueue.push(float32);
      return;
    }

    // 确保 AudioContext 正在运行
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }

    // 创建 AudioBuffer
    const buffer = this.audioContext.createBuffer(1, float32.length, this.sampleRate);
    const channelData = buffer.getChannelData(0);
    channelData.set(float32);

    // 创建音频源
    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    // Connect to Master Gain instead of Destination directly
    source.connect(this.masterGainNode);

    // 计算开始时间
    const currentTime = this.audioContext.currentTime;
    let startTime: number;

    if (!this.isPlaying || this.nextPlayTime < currentTime) {
      // 第一个chunk或时间已过，立即开始
      startTime = currentTime + 0.03; // 小延迟避免爆音和吞音 (30ms)
      this.isPlaying = true;
      if (this.onPlaybackStateChangeCallback) {
        this.onPlaybackStateChangeCallback(true);
      }
      // console.log(`🎵 Starting audio playback at ${startTime.toFixed(3)}s`);
    } else {
      // 在上一个chunk之后无缝播放
      startTime = this.nextPlayTime;
    }

    // 计算这个chunk的持续时间
    const duration = float32.length / this.sampleRate;

    // 调度播放
    source.start(startTime);

    // 累计调度总时长 (用于对齐全局 Word Boundary 偏移量)
    this.totalScheduledDurationMs += duration * 1000;

    // 更新下一个chunk应该开始的时间
    this.nextPlayTime = startTime + duration;

    // 保存引用，用于停止
    this.scheduledSources.push(source);

    // console.log(`🎵 Scheduled chunk: ${(duration * 1000).toFixed(0)}ms, start at ${startTime.toFixed(3)}s, next at ${this.nextPlayTime.toFixed(3)}s, pending: ${this.scheduledSources.length}`);

    // 清理已完成的源
    source.onended = () => {
      // ✅ 关键：播完后立即断开，防止僵尸节点在 AudioContext 图中累积
      // iOS WebAudio 在大量连接节点时有 gain 渲染 bug，会导致音量渐进衰减直至无声
      try { source.disconnect(); } catch { }

      const index = this.scheduledSources.indexOf(source);
      if (index > -1) {
        this.scheduledSources.splice(index, 1);
      }

      // console.log(`✅ Audio chunk ended. Remaining: ${this.scheduledSources.length}`);

      // 如果没有更多调度的音频，重置状态
      if (this.scheduledSources.length === 0) {
        // console.log(`✅ All audio chunks completed`);
        this.isPlaying = false;
        if (this.onPlaybackStateChangeCallback) {
          this.onPlaybackStateChangeCallback(false);
        }
        this.nextPlayTime = 0;
        this.loggedFirstChunk = false; // 重置，下次播放再记录
      }
    };
  }

  /**
   * 重置播放计时器，通常在 AI 开始新一轮对话前调用。
   */
  resetPlaybackTimer() {
    this.totalScheduledDurationMs = 0;
    this.nextPlayTime = 0;
  }

  /**
   * 返回当前播放序列已进行的毫秒数。
   * 采用“总调度时长 - 剩余未播时长”的公式，比绝对时间相减更稳健。
   */
  getPlaybackTime(): number {
    if (!this.isPlaying) return 0;
    const currentTime = this.audioContext.currentTime;
    // 剩余还在缓冲区（未来）的时长
    const remainingBufferSec = Math.max(0, this.nextPlayTime - currentTime);
    
    // 获取硬件输出延迟（浏览器到扬声器的物理延迟补偿）
    const outputLatency = this.audioContext.outputLatency || 0;

    // 已播放时长 = 累计调度时长 - 剩余还没跑到的时长 - 硬件回路延迟
    const playedMs = this.totalScheduledDurationMs - (remainingBufferSec * 1000) - (outputLatency * 1000);
    return Math.max(0, playedMs / 1000); // 返回秒
  }

  pausePlayback() {
    if (this.isPlaying && !this.isPaused) {
      this.isPaused = true;
      if (this.audioContext.state === "running") {
        this.audioContext.suspend(); // 暂停 AudioContext
      }
      console.log("⏸️ Audio playback paused");
    }
  }

  /**
   * 立即停止播放（用于用户打断）
   * 使用快速淡出避免爆音
   */
  stopPlayback() {
    console.log(`🛑 Stopping playback. Scheduled sources: ${this.scheduledSources.length}, Queue: ${this.audioQueue.length}`);

    this.stopRequested = true;

    // ✅ 修复：重置 masterGainNode 的任何残留的 gain 自动化，防止 iOS WebAudio gain 渐降 bug
    const currentTime = this.audioContext.currentTime;
    this.masterGainNode.gain.cancelScheduledValues(currentTime);
    this.masterGainNode.gain.setValueAtTime(1.0, currentTime);

    const fadeOutDuration = 0.05; // 50ms 快速淡出

    for (const source of this.scheduledSources) {
      try {
        // ✅ 修复：路由临时 GainNode 通过 masterGainNode 而不是直接连 destination
        // 这样可以避免绕过 masterGainNode 导致后续音频音量异常
        const gainNode = this.audioContext.createGain();
        source.disconnect(); // 先断开原来的连接（从 masterGainNode 断开）
        source.connect(gainNode);
        gainNode.connect(this.masterGainNode); // ← 修复：通过 masterGainNode 而非 destination

        // 淡出
        gainNode.gain.setValueAtTime(1, currentTime);
        gainNode.gain.linearRampToValueAtTime(0, currentTime + fadeOutDuration);

        // 淡出后停止
        source.stop(currentTime + fadeOutDuration);

        // 淡出完成后立即释放临时 GainNode，防止节点泄漏
        setTimeout(() => {
          try { gainNode.disconnect(); } catch { }
        }, (fadeOutDuration + 0.02) * 1000);
      } catch (e) {
        // 已经停止或断开的源，忽略错误
        try {
          source.stop();
        } catch { }
      }
    }

    // 清空引用
    this.scheduledSources = [];
    this.audioQueue = [];
    const wasPlaying = this.isPlaying;
    this.isPlaying = false;
    if (wasPlaying && this.onPlaybackStateChangeCallback) {
      this.onPlaybackStateChangeCallback(false);
    }
    this.isPaused = false;
    this.nextPlayTime = 0;
    this.totalScheduledDurationMs = 0;

    if (this.audioContext.state === "suspended") {
      this.audioContext.resume(); // 确保不锁死
    }

    console.log("🛑 Audio playback stopped with fade-out");

    // 延迟重置 stopRequested，等待淡出完成
    setTimeout(() => {
      this.stopRequested = false;
      console.log("🔄 stopRequested reset to false, ready for next playback");
    }, fadeOutDuration * 1000 + 50);
  }

  /**
   * 完全停止播放（用于会话结束）
   * 允许当前正在播放的音频完成
   */
  stopPlaybackGently() {
    console.log(`🔇 Gently stopping playback. Will let current audio finish.`);
    this.stopRequested = true;
    this.audioQueue = []; // 清空队列，但不停止已调度的音频

    // 等待所有已调度的音频播放完成
    // 它们会在 onended 回调中自动清理
  }

  stopMicCapture() {
    this.isRecording = false;

    // DO NOT Disconnect nodes or stop tracks here.
    // Just mute the tracks to keep the hardware (and OS mode) active.
    if (this.micStream) {
      console.log("🛑 Soft-stopping Mic (Muting tracks, keeping stream alive)");
      this.micStream.getAudioTracks().forEach((track) => {
        track.enabled = false; // Mute, but do not stop.
      });
    }

    // Note: processorNode stops firing 'onaudioprocess' effectively when tracks are muted or
    // simply because we check `this.isRecording` inside the handler.
  }

  /**
   * 强制 iOS 重新读取 AVAudioSession 的音频路由配置。
   * 用于从后台恢复时：iOS 会 interrupt AVAudioSession 导致路由丢失（走听筒），
   * resetAudioSession() 恢复了系统级配置，但已运行的 AudioContext 不会自动更新。
   * 通过 suspend → resume 迫使 AudioContext 重新绑定音频路由。
   */
  async refreshAudioRouting() {
    if (!this.audioContext || this.audioContext.state === 'closed') return;
    try {
      await this.audioContext.suspend();
      await this.audioContext.resume();
      console.log('[AudioManager] AudioContext suspended/resumed to refresh audio routing');
    } catch (err) {
      console.warn('[AudioManager] refreshAudioRouting error:', err);
    }
  }

  /**
   * 停止会话（仅停止录音和播放，保持 AudioContext 运行以避免 FigXPC 错误）
   * 注意：在 iOS 上频繁 suspend/resume 或者 close/create AudioContext 都会导致 FigXPC 错误
   * 所以最佳策略是保持 AudioContext running，只管理 Microphone Tracks
   */
  async stopSession() {
    console.log('[AudioManager] Stopping session (keeping context running)...');

    // 1. 停止录音 (Releases Microphone Hardware)
    this.stopMicCapture();

    // 2. 停止播放
    this.stopPlayback();
  }

  /**
   * 释放麦克风硬件和音频节点，但不关闭 AudioContext。
   * 用于延迟清理场景：避免 iOS 因 audioContext.close() 重置音频会话，
   * 导致后续新 AudioContext 的播放音量异常（路由到听筒而非扬声器）。
   * 旧 AudioContext 会在 AudioManager 引用释放后被 GC 自然回收。
   */
  async releaseHardware() {
    this.stopPlayback();
    this.isRecording = false;

    if (this.micStream) {
      console.log("🛑 Hard-stopping Mic (Releasing hardware)");
      this.micStream.getTracks().forEach(track => track.stop());
      this.micStream = null;
    }

    if (this.gainNode) { this.gainNode.disconnect(); this.gainNode = null; }
    if (this.processorNode) { this.processorNode.disconnect(); this.processorNode = null; }
    if (this.sourceNode) { this.sourceNode.disconnect(); this.sourceNode = null; }

    // 必须关闭 AudioContext：iOS 不允许两个 AudioContext 同时操作麦克风，
    // 不关闭旧的会导致新 AudioContext 的 getUserMedia 返回静音流。
    // 关闭后 iOS 会重置音频会话（丢失 defaultToSpeaker），
    // 需要在创建新 AudioContext 前重新配置（见 resetAudioSession）。
    // 必须 await：否则 close() 会在 resetAudioSession 之后才完成，又把音频会话重置掉。
    if (this.audioContext && this.audioContext.state !== 'closed') {
      try {
        await this.audioContext.close();
        console.log('[AudioManager] AudioContext closed in releaseHardware');
      } catch (err) {
        console.warn('[AudioManager] AudioContext close error:', err);
      }
    }

    this.audioCallback = null;
    console.log("🛑 AudioManager hardware released");
  }

  /**
   * 彻底清理所有资源（用于组件卸载时）
   */
  async cleanup() {
    // 停止播放
    this.stopPlayback();

    // Hard Stop Microphone (Release Hardware)
    this.isRecording = false;
    if (this.micStream) {
      console.log("🛑 Hard-stopping Mic (Releasing hardware)");
      this.micStream.getTracks().forEach(track => track.stop());
      this.micStream = null;
    }

    if (this.gainNode) { this.gainNode.disconnect(); this.gainNode = null; }
    if (this.processorNode) { this.processorNode.disconnect(); this.processorNode = null; }
    if (this.sourceNode) { this.sourceNode.disconnect(); this.sourceNode = null; }

    // 关闭 AudioContext（如果可能）
    if (this.audioContext && this.audioContext.state !== 'closed') {
      try {
        await this.audioContext.close();
        console.log('[AudioManager] AudioContext closed successfully');
      } catch (err) {
        console.warn('[AudioManager] 关闭 AudioContext 时出错:', err);
      }
    }

    // 清理回调
    this.audioCallback = null;

    console.log("🛑 AudioManager 资源已彻底清理");
  }
}
